"use client";
import React, { useState, useEffect } from "react";
import {
  Search,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Users,
  Mail,
  Shield,
} from "lucide-react";
import { useUsers } from "../context/UserContext";

const cx = (...c) => c.filter(Boolean).join(" ");

const Pill = ({ children, tone = "gray", size = "md" }) => {
  const toneMap = {
    red: "bg-red-50 text-red-700 ring-red-100",
    blue: "bg-blue-50 text-blue-700 ring-blue-100",
    green: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    gray: "bg-gray-50 text-gray-700 ring-gray-200",
    amber: "bg-amber-50 text-amber-700 ring-amber-100",
    violet: "bg-violet-50 text-violet-700 ring-violet-100",
    yellow: "bg-yellow-50 text-yellow-700 ring-yellow-100",
  };
  const sizeMap = {
    sm: "px-2.5 py-1 text-[11px]",
    md: "px-3 py-1.5 text-xs",
  };
  return (
    <span
      className={cx(
        "inline-flex items-center rounded-full font-extrabold ring-1 whitespace-nowrap",
        toneMap[tone] || toneMap.gray,
        sizeMap[size] || sizeMap.md
      )}
    >
      {children}
    </span>
  );
};

const SectionDivider = ({ label, right }) => (
  <div className="flex items-center gap-3 my-8">
    {label ? (
      <span className="inline-flex items-center rounded-full bg-white px-4 py-2 text-xs font-extrabold text-gray-800 ring-1 ring-gray-200 shadow-sm">
        {label}
      </span>
    ) : null}
    <div className="h-px flex-1 bg-gradient-to-r from-gray-200 via-gray-200 to-transparent" />
    {right ? <div className="flex items-center gap-2">{right}</div> : null}
  </div>
);

export default function UsersPage() {
  const {
    users,
    loading,
    error,
    fetchUsers,
    deleteUser,
    toggleUserStatus,
  } = useUsers();

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const ITEMS_PER_PAGE = 5;

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = users.filter(
    (user) =>
      user.name?.toLowerCase().includes(search.toLowerCase()) ||
      user.email?.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const start = (page - 1) * ITEMS_PER_PAGE;
  const currentUsers = filteredUsers.slice(start, start + ITEMS_PER_PAGE);

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white via-[#fff7f6] to-[#f9fafb] flex items-center justify-center">
        <div className="text-center">
          <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <Shield className="h-6 w-6 text-red-600" />
          </div>
          <h2 className="text-xl font-extrabold text-gray-900 mb-2">Error Loading Users</h2>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-[#fff7f6] to-[#f9fafb]">
      <div className="p-6 sm:p-8">
        {/* HERO SECTION */}
        <div className="rounded-3xl bg-white shadow-sm ring-1 ring-gray-200 overflow-hidden mb-8">
          <div className="relative p-7 sm:p-10">
            <div className="pointer-events-none absolute -top-28 -left-28 h-72 w-72 rounded-full bg-blue-200/25 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-28 -right-28 h-72 w-72 rounded-full bg-green-200/25 blur-3xl" />

            <div className="relative flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <div className="max-w-3xl">
                <div className="flex flex-wrap items-center gap-2">
                  <Pill tone="blue">
                    <Users className="h-4 w-4 mr-1" />
                    User Management
                  </Pill>
                  <Pill tone="gray">Admin Portal</Pill>
                  <Pill tone="green">
                    <Sparkles className="h-4 w-4 mr-1" />
                    Platform Users
                  </Pill>
                </div>

                <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-gray-900">
                  Users
                </h1>
                <p className="mt-3 text-gray-600 text-sm sm:text-base">
                  Manage platform users, monitor subscription status, and maintain user accounts across your system.
                </p>
              </div>

              <div className="flex items-center gap-3">
                <div className="rounded-2xl bg-gray-50 ring-1 ring-gray-200 px-4 py-3">
                  <div className="text-[11px] font-extrabold tracking-widest uppercase text-gray-600">Total</div>
                  <div className="text-sm font-semibold text-gray-900 mt-1">
                    {filteredUsers.length} users
                  </div>
                </div>

                <div className="rounded-2xl bg-green-50 ring-1 ring-green-200 px-4 py-3">
                  <div className="text-[11px] font-extrabold tracking-widest uppercase text-green-600">Subscribed</div>
                  <div className="text-sm font-semibold text-green-900 mt-1">
                    {filteredUsers.filter(u => u.is_subscribed).length} users
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>

        <SectionDivider
          label="Browse Users"
          right={
            <div className="flex items-center gap-2">
              {search && <Pill tone="amber" size="sm">Search: {search}</Pill>}
              <Pill tone="blue" size="sm">Page {page} of {totalPages}</Pill>
            </div>
          }
        />

        {/* SEARCH */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-10 pr-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none"
            />
          </div>
        </div>

        {/* TABLE */}
        <div className="rounded-3xl bg-white ring-1 ring-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="px-6 py-4 text-left text-xs font-extrabold tracking-widest uppercase text-gray-600">
                    User
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-extrabold tracking-widest uppercase text-gray-600">
                    Email
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-extrabold tracking-widest uppercase text-gray-600">
                    Subscribed
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-extrabold tracking-widest uppercase text-gray-600">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-extrabold tracking-widest uppercase text-gray-600">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-red-600 border-t-transparent"></div>
                        <span className="text-sm text-gray-600">Loading users...</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <>
                    {currentUsers.map((user, index) => (
                  <tr
                    key={user.id}
                    className={cx(
                      "hover:bg-gray-50 transition-colors",
                      index !== currentUsers.length - 1 ? "border-b border-gray-100" : ""
                    )}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-2xl bg-blue-100 flex items-center justify-center">
                          <Users className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="font-extrabold text-gray-900">{user.name}</div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-2xl bg-gray-100 flex items-center justify-center">
                          <Mail className="h-5 w-5 text-gray-600" />
                        </div>
                        <div className="font-medium text-gray-700">{user.email}</div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <Pill
                        tone={user.is_subscribed ? "green" : "gray"}
                        size="sm"
                      >
                        {user.is_subscribed ? "Yes" : "No"}
                      </Pill>
                    </td>

                    <td className="px-6 py-4">
                      <button
                        onClick={() => toggleUserStatus(user.id, user.status)}
                        className="transition-all duration-200"
                      >
                        <Pill
                          tone={user.status === "Active" ? "green" : "yellow"}
                          size="sm"
                        >
                          {user.status}
                        </Pill>
                      </button>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => deleteUser(user.id)}
                          className="p-2 rounded-2xl hover:bg-red-50 text-red-600 hover:text-red-700 transition-colors"
                          aria-label="Delete user"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                    </tr>
                    ))}

                    {currentUsers.length === 0 && (
                      <tr>
                        <td colSpan="5" className="px-6 py-12 text-center">
                          <div className="flex flex-col items-center gap-3">
                            <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center">
                              <Users className="h-6 w-6 text-gray-400" />
                            </div>
                            <div>
                              <div className="font-extrabold text-gray-900">No users found</div>
                              <p className="mt-1 text-sm text-gray-600">
                                {search ? "Try adjusting your search terms." : "No users have registered yet."}
                              </p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* PAGINATION */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-3 mt-8">
            <button
              onClick={() => setPage(Math.max(page - 1, 1))}
              disabled={page === 1}
              className={cx(
                "p-3 rounded-2xl ring-1 ring-gray-200 transition-colors",
                page === 1
                  ? "opacity-50 cursor-not-allowed bg-gray-50"
                  : "bg-white hover:bg-gray-50"
              )}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className={cx(
                    "px-4 py-2 rounded-2xl text-sm font-extrabold transition-colors",
                    pageNum === page
                      ? "bg-red-600 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50 ring-1 ring-gray-200"
                  )}
                >
                  {pageNum}
                </button>
              ))}
            </div>

            <button
              onClick={() => setPage(Math.min(page + 1, totalPages))}
              disabled={page === totalPages}
              className={cx(
                "p-3 rounded-2xl ring-1 ring-gray-200 transition-colors",
                page === totalPages
                  ? "opacity-50 cursor-not-allowed bg-gray-50"
                  : "bg-white hover:bg-gray-50"
              )}
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
